import { Component, OnDestroy, OnInit } from "@angular/core";
import {
  FormArray,
  FormGroup,
  UntypedFormBuilder,
  Validators,
} from "@angular/forms";
import { Store } from "@ngrx/store";
import { SiteSection } from "@serasa/shared/enums/site-section.enum";
import { AnalyticsService } from "@serasa/shared/services/analytics.service";
import { SearchInfo, Utils } from "@serasa/shared/utils/utils";
import { Observable, ReplaySubject, takeUntil } from "rxjs";
import { SedsModalService } from "@serasa/shared/seds";
import { MinMaxLengthRegexValidator } from "../../../../../shared/validators";
import {
  CloneLogon,
  CloneLogonResult,
} from "../../interfaces/clone-logon.interface";
import { CloneableLogonSearch } from "../../interfaces/cloneable-logon-search.interface";
import { CloneLogonSearchItem } from "../../interfaces/clone-logon-search-item.interface";
import { CloneLogonSearchState } from "../../interfaces/user-state.interface";
import { PmeLogonsCount } from "../../../recertification/interfaces/user-me.interface";
import {
  addClonedUser,
  cleanClonedUser,
  closeSideNav,
  loadCloneableLogonSearchData,
  clearCloneLogonSearch,
  loadUserMeInfo,
  searchCloneLogonByNameEmailLogon,
} from "../../state/actions/user.actions";
import {
  selectCloneableLogonSearch,
  selectCloneLogonSearch,
  selectClonedLogon,
  selectUserMeInfo,
} from "../../state/selectors/user.selectors";
import { UserAddMessagesHelper } from "./helpers/user-add-messages.helper";
import {
  IUserAddSearchMessagesHelper,
  UserAddSearchMessagesHelper,
} from "./helpers/user-add-search-messages.helper";
import { UserAddStep } from "./enums/user-add-step.enum";
import { UserAddSuccessModalComponent } from "../../../dashboard/components/user-add-success-modal/user-add-success-modal.component";
import {
  createEmailIdEntry,
  EmailIdEntryForm,
} from "./components/email-id-list/email-id-list.component";

@Component({
  selector: "pl-user-add",
  templateUrl: "./user-add.component.html",
  styleUrls: ["./user-add.component.scss"],
  standalone: false,
})
export class UserAddComponent implements OnInit, OnDestroy {
  public cloneableLogon$: Observable<CloneableLogonSearch | undefined>;
  public clonedLogon$: Observable<CloneLogon | undefined>;
  public cloneLogonSearch$: Observable<CloneLogonSearchState | undefined>;
  public userMeInfo$ = this.store.select(selectUserMeInfo);
  public searchCloneableLogonForm: FormGroup;
  public entriesForm = new FormArray<FormGroup<EmailIdEntryForm>>([
    createEmailIdEntry(true),
  ]);
  public errorMessage: IUserAddSearchMessagesHelper | undefined;
  public pageName?: string;

  private readonly destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);

  public currentStep: UserAddStep = UserAddStep.SEARCH;
  public readonly UserAddStep = UserAddStep;

  public inputError = false;
  public isTooManyResultsError = false;
  public searchExecuted = false;
  public selectedLogon: CloneLogonSearchItem | null = null;
  public searchResults: CloneLogonSearchItem[] = [];
  public searchResultsLoading = false;

  public searchLoading = false;
  public createLogonLoading = false;

  public cloneableLogonData: CloneableLogonSearch | undefined;
  public hasError = false;
  public pmeLogonsCount: PmeLogonsCount | undefined;
  public pmeLimitsUnavailable = false;
  public failedCloneResults: CloneLogonResult[] = [];

  constructor(
    private readonly store: Store,
    private readonly _fb: UntypedFormBuilder,
    private readonly _analyticsService: AnalyticsService,
    private readonly _dialog: SedsModalService,
  ) {
    this.cloneableLogon$ = this.store.select(selectCloneableLogonSearch);
    this.clonedLogon$ = this.store.select(selectClonedLogon);
    this.cloneLogonSearch$ = this.store.select(selectCloneLogonSearch);

    this.searchCloneableLogonForm = this._fb.group({
      logon: [
        "",
        [
          Validators.required,
          new MinMaxLengthRegexValidator(
            7,
            9,
            "minMaxError",
            "O logon do usuário deve possuir entre 7 e 9 caracteres",
          ).validate(),
        ],
      ],
    });
  }

  public ngOnInit(): void {
    this.pageName = `${SiteSection.CLONAR_USUARIO}:Busca`;
    this._analyticsService.doPageLoad(this.pageName);

    this.store.dispatch(clearCloneLogonSearch());
    this.store.dispatch(cleanClonedUser());

    this.cloneLogonSearch$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((searchState) => {
        if (!searchState) {
          return;
        }

        this.searchResultsLoading = searchState.loading;
        this.searchResults = searchState.results;

        if (searchState.error) {
          if (
            UserAddSearchMessagesHelper.isTooManyResultsError(searchState.error)
          ) {
            this.isTooManyResultsError = true;
            this.inputError = true;
            this.hasError = false;
            this.errorMessage = undefined;
          } else {
            this.isTooManyResultsError = false;
            this.inputError = false;
            this.hasError = true;
            this.errorMessage = new UserAddSearchMessagesHelper(
              searchState.error,
            ).error;
          }
          return;
        }

        this.isTooManyResultsError = false;

        if (!searchState.loading && this.searchExecuted) {
          if (searchState.results.length === 0) {
            this.hasError = true;
            this.errorMessage = {
              title: "Usuário não encontrado.",
              description:
                "Por favor, pesquise um novo usuário para espelhar os acessos.",
            };
          } else {
            this.hasError = false;
            this.errorMessage = undefined;
          }
        }
      });

    this.cloneableLogon$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((logonSearchData) => {
        this.searchLoading = false;
        this.cloneableLogonData = logonSearchData;

        if (logonSearchData?.logon) {
          this.store.dispatch(loadUserMeInfo());
          this.goToStep(UserAddStep.PRODUCTS);
        }

        if (logonSearchData?.error) {
          this.errorMessage = new UserAddSearchMessagesHelper(
            logonSearchData.error,
          ).error;
          this.hasError = true;
        }
      });

    this.userMeInfo$.pipe(takeUntil(this.destroyed$)).subscribe((userMe) => {
      this.pmeLogonsCount = userMe?.pmeLogonsCount;
      if (
        userMe?.channel?.channelDescription === "PME" &&
        !userMe.pmeLogonsCount
      ) {
        this.pmeLimitsUnavailable = true;
      }
    });

    this.clonedLogon$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((logonData) => {
        this.createLogonLoading = false;

        if (logonData?.failedResults?.length) {
          // Enquanto o backend não retorna erros específicos por e-mail
          // (ex.: 503/31 genérico), exibir apenas o banner vermelho genérico.
          // Quando o backend passar a diferenciar os motivos por e-mail,
          // remover a guarda `allGeneric` e exibir o banner amarelo com lista individual.
          const allGeneric = logonData.failedResults.every(
            (r) => r.error?.status === 503,
          );

          if (allGeneric) {
            this.failedCloneResults = [];
            this.errorMessage = new UserAddMessagesHelper(
              logonData.failedResults[0].error!,
            ).error;
            this.hasError = true;
          } else {
            this.failedCloneResults = logonData.failedResults;
            this.hasError = false;
          }
        }

        if (logonData?.logon?.length && !logonData.failedResults?.length) {
          this.store.dispatch(closeSideNav());
          this._openSuccessModal(logonData.successResults ?? []);
        }

        if (
          logonData?.error &&
          !logonData?.logon?.length &&
          !logonData?.failedResults?.length
        ) {
          this.errorMessage = new UserAddMessagesHelper(logonData.error).error;
          this.hasError = true;
        }
      });
  }

  private _openSuccessModal(results: CloneLogonResult[]): void {
    this._dialog.open(
      UserAddSuccessModalComponent,
      { results },
      { width: "600px" },
    );
  }

  public ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
    this.cleanCloneableLogonData();
  }

  public goToStep(step: UserAddStep): void {
    this.currentStep = step;
    this.updatePageName();
  }

  public goBack(): void {
    if (this.currentStep > UserAddStep.SEARCH) {
      this.clearCloneError();
      this.currentStep--;
      this.updatePageName();
    }
  }

  /** Limpa o estado de erro da clonagem para não vazar entre as etapas ao voltar. */
  private clearCloneError(): void {
    this.hasError = false;
    this.errorMessage = undefined;
    this.failedCloneResults = [];
  }

  public canGoBack(): boolean {
    return this.currentStep > UserAddStep.SEARCH;
  }

  private updatePageName(): void {
    switch (this.currentStep) {
      case UserAddStep.SEARCH:
        this.pageName = `${SiteSection.CLONAR_USUARIO}:Busca`;
        break;
      case UserAddStep.PRODUCTS:
        this.pageName = `${SiteSection.CLONAR_USUARIO}:Produtos`;
        break;
      case UserAddStep.FORM:
        this.pageName = `${SiteSection.CLONAR_USUARIO}:Criar`;
        break;
    }
    this._analyticsService.doPageLoad(this.pageName);
  }

  public getHeaderTitle(): string {
    switch (this.currentStep) {
      case UserAddStep.SEARCH:
        return "Selecione um usuário";
      case UserAddStep.PRODUCTS:
        return "Produtos habilitados";
      case UserAddStep.FORM:
        return "Adicione quem vai receber o acesso";
      default:
        return "Selecione um usuário";
    }
  }

  public closeSideNav(): void {
    this.store.dispatch(closeSideNav());
  }

  public onSelectionChanged(event: CloneLogonSearchItem): void {
    if (event?.logon) {
      this.selectedLogon = event;
      this.searchExecuted = true;
      this.searchCloneableLogonForm.patchValue({ logon: event.logon });
      this.hasError = false;
      this.errorMessage = undefined;
    }
  }

  public onSearchCriteriaChanged(): void {
    this.searchExecuted = false;
    this.selectedLogon = null;
    this.hasError = false;
    this.errorMessage = undefined;
    this.isTooManyResultsError = false;
    this.inputError = false;
    this.searchCloneableLogonForm.patchValue({ logon: "" });
  }

  public onSearchRequested(searchText: string): void {
    const trimmed = searchText?.trim() ?? "";

    if (!this.validateSearchValue(trimmed)) {
      return;
    }

    if (trimmed.length < 2) {
      this.hasError = true;
      this.errorMessage = {
        title: "Critério insuficiente",
        description: "Digite pelo menos 2 caracteres para pesquisar.",
      };
      return;
    }

    this.searchExecuted = true;
    this.selectedLogon = null;
    this.hasError = false;
    this.errorMessage = undefined;
    this.isTooManyResultsError = false;
    this.inputError = false;
    this.searchCloneableLogonForm.patchValue({ logon: "" });

    const searchInfo: SearchInfo = Utils.detectSearchType(trimmed);
    this.store.dispatch(
      searchCloneLogonByNameEmailLogon({
        searchValue: searchInfo.value,
        searchType: searchInfo.type,
      }),
    );
  }

  private validateSearchValue(value: string): boolean {
    if (!value) {
      this.inputError = false;
      return true;
    }

    if (value.includes("@")) {
      const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailPattern.test(value)) {
        this.inputError = true;
        this.hasError = true;
        this.errorMessage = {
          title: "Erro",
          description: "Digite um nome, e-mail ou logon válido",
        };
        return false;
      }
    }

    this.inputError = false;
    return true;
  }

  public clearSearch(): void {
    this.searchExecuted = false;
    this.selectedLogon = null;
    this.searchResults = [];
    this.inputError = false;
    this.isTooManyResultsError = false;
    this.hasError = false;
    this.errorMessage = undefined;
    this.searchCloneableLogonForm.patchValue({ logon: "" });
    this.store.dispatch(clearCloneLogonSearch());
  }

  public selectUser(): void {
    if (this.selectedLogon?.logon) {
      this.searchLoading = true;
      this.hasError = false;
      this.store.dispatch(
        loadCloneableLogonSearchData({ logon: this.selectedLogon.logon }),
      );
    } else {
      this.hasError = true;
      this.errorMessage = {
        title: "Selecione um usuário",
        description: "Procure pela pessoa usando nome, e-mail ou logon.",
      };
    }
  }

  public continueToForm(): void {
    if (this.isPmeLimitReached()) {
      return;
    }

    this.store.dispatch(loadUserMeInfo());
    this.goToStep(UserAddStep.FORM);
  }

  public isPmeLimitReached(): boolean {
    if (!this.pmeLogonsCount) {
      return false;
    }
    return (
      this.pmeLogonsCount.limitReached ||
      this.pmeLogonsCount.remainingCount <= 0
    );
  }

  public addUser(): void {
    if (this.isPmeLimitReached()) {
      this.hasError = true;
      this.errorMessage = {
        title: "Limite de logons atingido",
        description:
          "Você atingiu a quantidade máxima de usuários desse contrato. Entre em contato com a nossa equipe de atendimento.",
      };
      return;
    }

    const entries = this.entriesForm.controls
      .map((group) => ({
        email: group.controls.email.value.trim(),
        name: group.controls.accountName.value.trim(),
      }))
      .filter((entry) => !!entry.email);

    if (entries.length === 0) {
      this.hasError = true;
      this.errorMessage = {
        title: "E-mail não informado",
        description:
          "Insira pelo menos um endereço de e-mail para concluir o cadastro.",
      };
      this.entriesForm.markAllAsTouched();
      return;
    }

    if (this.hasInvalidEntries()) {
      this.entriesForm.markAllAsTouched();
      return;
    }

    this.createLogonLoading = true;
    this.hasError = false;
    this.failedCloneResults = [];
    this.store.dispatch(
      addClonedUser({
        payload: {
          entries: entries.map((entry) => ({
            email: entry.email,
            name: entry.name || undefined,
          })),
          ...this.searchCloneableLogonForm.value,
        },
      }),
    );
  }

  /**
   * Valida as linhas antes do envio: apenas a primeira linha exige e-mail. Linhas
   * subsequentes sem e-mail são ignoradas (não bloqueiam). Linhas com e-mail têm o
   * formato do e-mail e o limite da identificação validados.
   * Duplicados NÃO são bloqueados na interface (tratados pelo backend).
   */
  private hasInvalidEntries(): boolean {
    let invalid = false;

    this.entriesForm.controls.forEach((group, index) => {
      const emailControl = group.controls.email;
      const accountNameControl = group.controls.accountName;
      const email = emailControl.value.trim();

      if (!email) {
        if (index === 0) {
          emailControl.setErrors({
            ...(emailControl.errors ?? {}),
            required: true,
          });
          invalid = true;
        }
        return;
      }

      if (emailControl.invalid || accountNameControl.invalid) {
        invalid = true;
      }
    });

    return invalid;
  }

  public cleanCloneableLogonData(): void {
    this.searchCloneableLogonForm.reset();
    this.entriesForm.clear();
    this.entriesForm.push(createEmailIdEntry(true));
    this.store.dispatch(cleanClonedUser());
    this.store.dispatch(clearCloneLogonSearch());
  }

  public getNextButtonTitle(): string {
    switch (this.currentStep) {
      case UserAddStep.SEARCH:
        return "Selecionar usuário";
      case UserAddStep.PRODUCTS:
        return "Continuar";
      case UserAddStep.FORM:
        return "Criar usuários";
      default:
        return "Continuar";
    }
  }

  public isNextButtonDisabled(): boolean {
    if (this.currentStep === UserAddStep.SEARCH) {
      return !this.selectedLogon?.logon;
    }
    return (
      this.currentStep === UserAddStep.PRODUCTS && this.isPmeLimitReached()
    );
  }

  public onNextButtonClick(): void {
    switch (this.currentStep) {
      case UserAddStep.SEARCH:
        this.selectUser();
        break;
      case UserAddStep.PRODUCTS:
        this.continueToForm();
        break;
      case UserAddStep.FORM:
        this.addUser();
        break;
    }
  }
}
